/**
 * /portal/forgot-password and /portal/reset-password
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, CheckCircle2 } from 'lucide-react';
import { toast } from '../../stores';
import { onboardingApi } from '../../services/onboarding';
import { NexusButton } from '../../components/nexus-ui/NexusButton';
import { NexusInput } from '../../components/nexus-ui/NexusInput';
import { NexusCard } from '../../components/nexus-ui/NexusCard';

export const PortalForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onboardingApi.forgotPassword(email);
      setSent(true);
    } catch { toast.error('Something went wrong — please try again'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--nexus-bg-primary)' }}>
      <NexusCard variant="glass" hover={false} className="p-8 max-w-md w-full">
        {sent ? (
          <div className="text-center">
            <div className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-3"
              style={{ background: 'rgba(110,231,183,0.12)' }}>
              <CheckCircle2 size={28} style={{ color: '#10B981' }} />
            </div>
            <h2 className="font-heading text-xl font-semibold text-nexus-text-primary">Check your inbox</h2>
            <p className="text-[13px] text-nexus-text-secondary mt-1.5">
              If an account exists for <b>{email}</b>, we've sent a password reset link. It's valid for 30 minutes.
            </p>
            <Link to="/portal/login" className="inline-block mt-5 text-[13px] font-semibold text-nexus-accent-blue" data-testid="fp-back">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-nexus-text-primary">Reset your password</h1>
            <p className="text-[13px] text-nexus-text-secondary mt-1 mb-5">We'll email you a secure link.</p>
            <form onSubmit={onSubmit} className="space-y-3">
              <NexusInput label="Work email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" icon={<Mail size={14} />} required data-testid="fp-email" autoFocus />
              <NexusButton type="submit" className="w-full" isLoading={loading} data-testid="fp-submit">
                Send reset link
              </NexusButton>
            </form>
            <Link to="/portal/login" className="mt-4 block text-center text-[12px] text-nexus-text-secondary" data-testid="fp-cancel">
              Cancel
            </Link>
          </>
        )}
      </NexusCard>
    </div>
  );
};

export const PortalResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await onboardingApi.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/portal/login', { replace: true }), 1400);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not reset password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--nexus-bg-primary)' }}>
      <NexusCard variant="glass" hover={false} className="p-8 max-w-md w-full">
        {done ? (
          <div className="text-center">
            <CheckCircle2 size={36} style={{ color: '#10B981' }} className="mx-auto mb-2" />
            <h2 className="font-heading text-xl font-semibold text-nexus-text-primary">Password updated</h2>
            <p className="text-[13px] text-nexus-text-secondary mt-1">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-nexus-text-primary">Set a new password</h1>
            <p className="text-[13px] text-nexus-text-secondary mt-1 mb-5">Enter a fresh password for your account.</p>
            <form onSubmit={onSubmit} className="space-y-3">
              <NexusInput label="New password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters" icon={<Lock size={14} />} error={error} minLength={8} required
                data-testid="rp-password" />
              <NexusButton type="submit" className="w-full" isLoading={loading} data-testid="rp-submit">
                Update password
              </NexusButton>
            </form>
          </>
        )}
      </NexusCard>
    </div>
  );
};
